import { useState, useEffect, useRef } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { eventInfo } from '../../data/landingData';

// Pexels royalty-free video - Black man meditating on beach
const STOCK_VIDEO_URL = "https://videos.pexels.com/video-files/4908580/4908580-uhd_2732_1440_25fps.mp4";
const FALLBACK_VIDEO_URL = "https://videos.pexels.com/video-files/3571264/3571264-uhd_2560_1440_30fps.mp4";

const inspirationalPhrases = [
  { text: "Rise as Kings", subtext: "Embrace your power" },
  { text: "Build Legacy", subtext: "For generations to come" },
  { text: "Find Brotherhood", subtext: "You are not alone" },
  { text: "Transform Your Life", subtext: "In just 4 days" }
];

export const VideoSection = () => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Rotate through inspirational phrases
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPhraseIndex((prev) => (prev + 1) % inspirationalPhrases.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Intersection observer for fade-in effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const section = document.getElementById('video-section');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const currentPhrase = inspirationalPhrases[currentPhraseIndex];

  return (
    <section 
      id="video-section"
      className="relative h-[80vh] min-h-[600px] overflow-hidden"
      data-testid="video-section"
    >
      {/* Video Background */}
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
          poster="https://images.pexels.com/photos/4908577/pexels-photo-4908577.jpeg?auto=compress&cs=tinysrgb&w=1920"
        >
          <source src={STOCK_VIDEO_URL} type="video/mp4" />
          <source src={FALLBACK_VIDEO_URL} type="video/mp4" />
        </video>
        
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/50 to-[#0A0A0A]/90" />
        
        {/* Gold accent overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/10 via-transparent to-[#D4AF37]/10" />
      </div>

      {/* Content */}
      <div className={`relative z-10 h-full flex flex-col items-center justify-center text-center px-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        
        {/* Animated Phrase */}
        <div className="mb-8">
          <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.3em] text-xs font-bold mb-4 animate-pulse">
            ✦ The Reset Experience ✦
          </p>
          
          <h2 
            className="font-cormorant text-5xl md:text-7xl lg:text-8xl font-medium text-white mb-4 transition-all duration-700"
            key={currentPhrase.text}
            style={{ animation: 'fadeInUp 0.7s ease-out' }}
          >
            {currentPhrase.text}
          </h2>
          
          <p 
            className="font-manrope text-xl md:text-2xl text-zinc-300 transition-all duration-700"
            key={currentPhrase.subtext}
            style={{ animation: 'fadeInUp 0.7s ease-out 0.2s both' }}
          >
            {currentPhrase.subtext}
          </p>
        </div>

        {/* Event Info Bar */}
        <div className="glass rounded-full px-8 py-4 mb-8 flex flex-wrap items-center justify-center gap-4 md:gap-8">
          <div className="text-center">
            <p className="text-[#D4AF37] font-manrope text-xs uppercase tracking-wider">When</p>
            <p className="text-white font-manrope font-medium">{eventInfo.dates}</p>
          </div>
          <div className="hidden md:block w-px h-8 bg-white/20" />
          <div className="text-center">
            <p className="text-[#D4AF37] font-manrope text-xs uppercase tracking-wider">Where</p>
            <p className="text-white font-manrope font-medium">{eventInfo.location}</p>
          </div>
          <div className="hidden md:block w-px h-8 bg-white/20" />
          <div className="text-center">
            <p className="text-[#D4AF37] font-manrope text-xs uppercase tracking-wider">Investment</p>
            <p className="text-white font-manrope font-medium">${eventInfo.price.toLocaleString()}</p>
          </div>
        </div>

        {/* CTA */}
        <a
          href="#pricing"
          className="bg-[#D4AF37] text-[#0A0A0A] px-10 py-4 rounded-full font-manrope font-bold text-lg hover:bg-[#B8860B] transition-all hover:-translate-y-1 gold-glow"
          data-testid="video-section-cta"
        >
          Begin Your Journey
        </a>

        {/* Phrase indicators */}
        <div className="flex gap-2 mt-8">
          {inspirationalPhrases.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentPhraseIndex(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentPhraseIndex 
                  ? 'bg-[#D4AF37] w-6' 
                  : 'bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Video Controls */}
      <div className="absolute bottom-6 right-6 flex gap-2 z-20">
        <button
          onClick={togglePlay}
          className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          aria-label={isPlaying ? 'Pause video' : 'Play video'}
          data-testid="video-play-btn"
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <button
          onClick={toggleMute}
          className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          aria-label={isMuted ? 'Unmute video' : 'Mute video'}
          data-testid="video-mute-btn"
        >
          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce z-20">
        <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2">
          <div className="w-1 h-2 bg-white/50 rounded-full animate-pulse" />
        </div>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};
